import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { type VariantProps, cva } from "class-variance-authority"
import { cn } from "design/lib"

const badgeVariants = cva(
  "group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [a]:hover:ring-2 [a]:hover:ring-offset-1 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground [a]:hover:bg-primary/80 [a]:hover:ring-primary/40",
        secondary:
          "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80 [a]:hover:ring-secondary/40",
        destructive:
          "bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20 [a]:hover:ring-destructive/40",
        outline:
          "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground [a]:hover:ring-border",
        ghost:
          "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",

        pending:
          "bg-muted text-muted-foreground [a]:hover:bg-accent [a]:hover:ring-muted-foreground/30",
        success:
          "bg-success/10 text-success dark:text-success-light [a]:hover:bg-success/20 [a]:hover:ring-success/40",
        warning:
          "bg-spark-yellow/10 text-yellow-700 dark:bg-spark-yellow/10 dark:text-spark-yellow [a]:hover:bg-spark-yellow/20 [a]:hover:ring-spark-yellow/40 dark:[a]:hover:bg-spark-yellow/20",
        error:
          "bg-destructive/10 text-destructive dark:bg-destructive/10 dark:text-destructive [a]:hover:bg-destructive/20 [a]:hover:ring-destructive/40 dark:[a]:hover:bg-destructive/20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function Badge({
  className,
  variant = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ className, variant })),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants }
